/**
 * recommendationEngine.js
 *
 * Pure logic — no React, no UI. Takes usage history + plan data,
 * returns a recommendation object the sidebar renders.
 *
 * Exported:
 *   computeRecommendation(params) → Recommendation
 *
 * Recommendation shape:
 * {
 *   type:        'upgrade' | 'downgrade' | 'on_track' | 'building' | 'over_quota'
 *   signal:      'projected' | 'peak' | 'average' | 'low_usage' | null
 *   confidence:  'high' | 'medium' | 'low'         // how much history we have
 *   targetPlan:  { name, quota, price } | null      // plan to move to
 *   currentPlan: { name, quota, price }
 *   savings:     number | null                      // monthly $ delta (positive = saving)
 *   extraCost:   number | null                      // monthly $ delta for upgrade
 *   reason:      string                             // human-readable explanation
 *   usageSummary: {
 *     currentViews:  number,
 *     projected:     number,
 *     peakMonthly:   number,
 *     avgMonthly:    number,
 *     monthsOfData:  number,
 *   }
 * }
 */

/**
 * @param {object} params
 * @param {Array<{billing_month: string, widget_views: number}>} params.history
 *   Monthly usage rows, sorted ascending. Sparse — may have gaps.
 * @param {number}  params.currentViews    Views so far this month
 * @param {number}  params.daysElapsed     Days elapsed in current month (>=1)
 * @param {number}  params.daysInMonth     Total days in current month
 * @param {object}  params.currentPlan     { name, quota, price }
 * @param {Array}   params.allPlans        [{ name, quota, price }] sorted ascending by quota
 * @param {string}  params.billingSettingsPath  e.g. '/settings/billing'
 */
export function computeRecommendation({
  history = [],
  currentViews = 0,
  daysElapsed = 1,
  daysInMonth = 30,
  currentPlan,
  allPlans = [],
}) {
  // ── 1. Sanitise history ──────────────────────────────────────────────────
  const thisMonth = formatYearMonth(new Date());
  const pastRows = history.filter(
    (r) => r.billing_month !== thisMonth && Number(r.widget_views) > 0
  );
  const recentPastRows = pastRows.slice(-3);
  const monthsOfData = pastRows.length;

  // ── 2. Compute usage signals ─────────────────────────────────────────────
  const projected = Math.round(
    (currentViews / Math.max(daysElapsed, 1)) * daysInMonth
  );
  const pastViews = pastRows.map((r) => Number(r.widget_views) || 0);
  const recentPastViews = recentPastRows.map(
    (r) => Number(r.widget_views) || 0
  );
  const peakMonthly = pastViews.length > 0 ? Math.max(...pastViews) : projected;
  const avgMonthly = pastViews.length > 0
    ? Math.round(pastViews.reduce((s, v) => s + v, 0) / pastViews.length)
    : projected;
  const recentPeakMonthly =
    recentPastViews.length > 0 ? Math.max(...recentPastViews) : projected;
  const recentAvgMonthly =
    recentPastViews.length > 0
      ? Math.round(
          recentPastViews.reduce((sum, value) => sum + value, 0) /
            recentPastViews.length
        )
      : avgMonthly;

  const quota = currentPlan.quota;
  const hasUnlimitedQuota = quota === -1;

  const usageSummary = {
    currentViews,
    projected,
    peakMonthly,
    avgMonthly,
    monthsOfData,
  };

  if (hasUnlimitedQuota) {
    return {
      type: 'on_track',
      signal: null,
      confidence: monthsOfData >= 3 ? 'high' : 'medium',
      targetPlan: null,
      currentPlan,
      savings: null,
      extraCost: null,
      reason: 'Your plan includes unlimited monthly views.',
      usageSummary,
    };
  }

  // ── 3. Not enough history ────────────────────────────────────────────────
  if (monthsOfData < 2 && currentViews === 0) {
    return {
      type: 'building',
      signal: null,
      confidence: 'low',
      targetPlan: null,
      currentPlan,
      savings: null,
      extraCost: null,
      reason: 'We\'re building your usage history. Check back after a few weeks of activity.',
      usageSummary,
    };
  }

  // ── 4. Currently over quota ──────────────────────────────────────────────
  if (currentViews > quota) {
    const nextPlan = findUpgradePlan(allPlans, currentPlan, currentViews);
    return {
      type: 'over_quota',
      signal: 'projected',
      confidence: 'high',
      targetPlan: nextPlan,
      currentPlan,
      savings: null,
      extraCost: nextPlan ? nextPlan.price - currentPlan.price : null,
      reason: `You've exceeded your ${fmtN(quota)} view quota this month.`,
      usageSummary,
    };
  }

  // ── 5. Upgrade signals ───────────────────────────────────────────────────

  // Signal A: projected end-of-month > 95%
  if (pct(projected, quota) >= 95) {
    const target = findUpgradePlan(allPlans, currentPlan, projected);
    return {
      type: 'upgrade',
      signal: 'projected',
      confidence: 'high',
      targetPlan: target,
      currentPlan,
      savings: null,
      extraCost: target ? target.price - currentPlan.price : null,
      reason: `Your projected usage this month is ${fmtN(projected)} views — close to your ${fmtN(quota)} limit.`,
      usageSummary,
    };
  }

  // Signal B: peak month > 90%
  if (monthsOfData >= 1 && pct(peakMonthly, quota) >= 90) {
    const target = findUpgradePlan(allPlans, currentPlan, peakMonthly);
    return {
      type: 'upgrade',
      signal: 'peak',
      confidence: monthsOfData >= 3 ? 'high' : 'medium',
      targetPlan: target,
      currentPlan,
      savings: null,
      extraCost: target ? target.price - currentPlan.price : null,
      reason: `Your peak month reached ${fmtN(peakMonthly)} views — ${pct(peakMonthly, quota)}% of your quota.`,
      usageSummary,
    };
  }

  // Signal C: 3-month average > 80%
  if (monthsOfData >= 3 && pct(avgMonthly, quota) >= 80) {
    const target = findUpgradePlan(allPlans, currentPlan, avgMonthly);
    return {
      type: 'upgrade',
      signal: 'average',
      confidence: 'medium',
      targetPlan: target,
      currentPlan,
      savings: null,
      extraCost: target ? target.price - currentPlan.price : null,
      reason: `Your average monthly usage is ${fmtN(avgMonthly)} views — ${pct(avgMonthly, quota)}% of your quota.`,
      usageSummary,
    };
  }

  // ── 6. Downgrade signal ───────────────────────────────────────────────────
  // Require at least 3 recent months of consistently low usage.
  if (monthsOfData >= 3) {
    const maxUsage = Math.max(recentPeakMonthly, projected);
    if (pct(maxUsage, quota) < 35 && pct(recentAvgMonthly, quota) < 30) {
      const target = findDowngradePlan(allPlans, currentPlan, maxUsage);
      if (target) {
        return {
          type: 'downgrade',
          signal: 'low_usage',
          confidence: monthsOfData >= 6 ? 'high' : 'medium',
          targetPlan: target,
          currentPlan,
          savings: currentPlan.price - target.price,
          extraCost: null,
          reason: `Your recent usage has stayed below ${pct(maxUsage, quota)}% of your quota.`,
          usageSummary,
        };
      }
    }
  }

  // ── 7. On track ───────────────────────────────────────────────────────────
  return {
    type: 'on_track',
    signal: null,
    confidence: monthsOfData >= 3 ? 'high' : 'medium',
    targetPlan: null,
    currentPlan,
    savings: null,
    extraCost: null,
    reason: `Your usage is well within your ${fmtN(quota)} monthly quota.`,
    usageSummary,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function pct(value, total) {
  if (!total || total === -1) return 0;
  return Math.round((value / total) * 100);
}

function fmtN(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatYearMonth(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Find the cheapest plan that comfortably covers targetUsage (with 20% headroom).
 * Must be higher than currentPlan.
 */
function findUpgradePlan(allPlans, currentPlan, targetUsage) {
  const needed = Math.ceil(targetUsage * 1.2);
  return (
    allPlans
      .filter(
        (p) =>
          (p.quota === -1 || p.quota >= needed) &&
          currentPlan.quota !== -1 &&
          (p.quota === -1 || p.quota > currentPlan.quota)
      )
      .sort((a, b) => {
        if (a.quota === -1) return 1;
        if (b.quota === -1) return -1;
        return a.quota - b.quota;
      })[0] || null
  );
}

/**
 * Find the highest plan below currentPlan whose quota still covers maxUsage
 * with comfortable headroom (50% — generous buffer for downgrade suggestions).
 */
function findDowngradePlan(allPlans, currentPlan, maxUsage) {
  const safeThreshold = maxUsage * 1.5;
  return (
    allPlans
      .filter(
        (p) =>
          p.quota !== -1 &&
          (currentPlan.quota === -1 || p.quota < currentPlan.quota) &&
          p.quota >= safeThreshold
      )
      .sort((a, b) => b.quota - a.quota)[0] || null
  );
}
